/**
 * §A.1 + §A.2 · Un club, entero.
 *
 * Lo que se mira antes de llamar, en el orden en que se mira: **quién es**, **si
 * usa la aplicación**, **qué tiene contratado** y **qué nos ha pagado**.
 *
 * ## El uso va antes que el dinero, y es deliberado
 *
 * Un club que paga y no entra es una baja que todavía no ha ocurrido. El plan y el
 * importe no lo dicen; `sessions_held`, `roll_calls` y `guardians_with_app` sí. Por
 * eso el bloque de uso está arriba y el de suscripción debajo.
 *
 * ## Lo contratado y lo cobrado son dos cifras distintas
 *
 * Y aquí se ven las dos, a propósito. `SubscriptionViewDTO` dice lo que el club
 * **debería** pagar cada mes; `collected_cents` dice lo que ha entrado de verdad.
 * Sin pasarela (H-14) no hay webhook que concilie una con otra: la diferencia la
 * lee una persona, así que tiene que estar delante.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { euros, fecha, fechaYHora } from '@/shared/ui/formato';
import { aCentimos } from '@/club/cuotas/tipos';
import { AccionesDelClub } from '@/backoffice/AccionesDelClub';
import { CronologiaDelClub } from '@/backoffice/CronologiaDelClub';
import { InterruptoresDelClub } from '@/backoffice/InterruptoresDelClub';
import { CambioDeDueno } from '@/backoffice/CambioDeDueno';
import { InvitacionDelDueno } from '@/backoffice/InvitacionDelDueno';
import { SuscripcionDelClub } from '@/backoffice/SuscripcionDelClub';
import {
  etiquetaDeEstadoDeClub,
  etiquetaDeMetodoDePago,
  METODOS_DE_PAGO,
  RIESGOS,
  SITUACIONES_DEL_PAGO,
  type ClubDetalle,
  type MetodoDePago,
  type PaginaDePagos,
  type UsoDelClub,
  type VistaDeSuscripcion,
} from '@/backoffice/tipos';

/** `2026-09`, el mes en curso. Es el que suele cubrir un cobro que se anota hoy. */
const mesActual = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * El último día que cubre un cobro del mes `2026-09`, según la periodicidad:
 * `2026-09-30` si es mensual, `2026-11-30` si es trimestral, `2027-08-31` si es
 * anual. Es solo la propuesta: quien anota lo cambia si el club ha pagado otra cosa.
 */
export const finDeCobertura = (periodo: string, meses: number): string => {
  const [a, m] = periodo.split('-').map(Number) as [number, number];
  // El día 0 del mes siguiente al último cubierto es el último día de ese mes.
  const d = new Date(Date.UTC(a, m - 1 + meses, 0));
  return d.toISOString().slice(0, 10);
};

const mesesDe = (periodicidad: string | undefined): number =>
  periodicidad === 'annual' ? 12 : periodicidad === 'quarterly' ? 3 : 1;

const hoy = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

export function FichaDeClub({
  clubId,
  alCerrar,
  alAbrirPersona,
  alEntrarEnSoporte,
}: {
  clubId: string;
  alCerrar: () => void;
  alAbrirPersona: (userId: string) => void;
  alEntrarEnSoporte: (clubId: string) => void;
}) {
  const cache = useQueryClient();
  const [anotando, setAnotando] = useState(false);
  const [importe, setImporte] = useState('');
  const [metodo, setMetodo] = useState<MetodoDePago>('transfer');
  const [cuando, setCuando] = useState(hoy);
  const [periodo, setPeriodo] = useState(mesActual);
  const [referencia, setReferencia] = useState('');
  // Vacío = el que propone el mes y la periodicidad; se escribe solo si se toca.
  const [hasta, setHasta] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const club = useQuery({
    queryKey: ['admin', 'club', clubId],
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .academies({ id: clubId })
        .get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ClubDetalle;
    },
  });

  const uso = useQuery({
    queryKey: ['admin', 'uso', clubId],
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .academies({ id: clubId })
        .usage.get({ query: { window_days: 30 } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as UsoDelClub;
    },
  });

  const suscripcion = useQuery({
    queryKey: ['admin', 'suscripcion', clubId],
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .academies({ id: clubId })
        .subscription.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as VistaDeSuscripcion;
    },
  });

  const pagos = useQuery({
    queryKey: ['admin', 'pagos', clubId],
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .academies({ id: clubId })
        .payments.get({ query: { limit: 24 } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDePagos;
    },
  });

  const anotar = useMutation({
    mutationFn: async () => {
      const centimos = aCentimos(importe);
      if (centimos === null) {
        throw new ErrorDeApi({
          title: 'El importe no se entiende',
          status: 400,
          detail: 'En euros, con dos decimales como mucho: 29 o 29,50.',
          errors: [{ field: 'amount_cents', message: 'No es un importe' }],
        });
      }

      const r = await api.v1.admin.academies({ id: clubId }).payments.post(
        {
          amount_cents: centimos,
          method: metodo,
          paid_on: cuando,
          period: periodo === '' ? null : periodo,
          covers_until: hastaPropuesto,
          reference: referencia.trim() === '' ? null : referencia.trim(),
        },
        // Clave nueva por pulsación: una guardada replicaría el cobro anterior y
        // diría que entró dinero que no entró.
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setAnotando(false);
      setImporte('');
      setReferencia('');
      setHasta('');
      void cache.invalidateQueries({ queryKey: ['admin', 'pagos', clubId] });
      void cache.invalidateQueries({ queryKey: ['admin', 'clubes'] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const hastaPropuesto =
    hasta ||
    finDeCobertura(
      periodo || cuando.slice(0, 7),
      mesesDe(suscripcion.data?.active?.billing_period)
    );

  if (club.isPending) return <Cargando filas={8} etiqueta="Cargando el club" />;

  if (club.isError || !club.data) {
    return (
      <PanelError
        problema={aProblema(club.error)}
        reintentar={() => void club.refetch()}
      />
    );
  }

  const c = club.data;
  const viva = suscripcion.data?.active ?? null;
  const resumen = pagos.data?.summary;

  // ── Anotar un cobro ──────────────────────────────────────────────────────
  if (anotando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          anotar.mutate();
        }}
      >
        <h1>Anotar un cobro de {c.trade_name}</h1>

        <p className="tenue">
          Sin pasarela no hay webhook que lo diga: lo marcas tú, y queda con tu
          correo al lado.
        </p>

        <div className="rejilla">
          <label>
            Cuánto ha pagado
            <input
              required
              inputMode="decimal"
              placeholder={viva ? String(viva.price_cents / 100) : '29,00'}
              value={importe}
              onChange={(e) => setImporte(e.target.value)}
            />
          </label>

          <label>
            Cómo
            <select
              value={metodo}
              onChange={(e) => setMetodo(e.target.value as MetodoDePago)}
            >
              {METODOS_DE_PAGO.map((m) => (
                <option key={m.valor} value={m.valor}>
                  {m.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label>
            Cuándo entró
            {/* La fecha del dinero, no la del tecleo: se anota días después. */}
            <input
              type="date"
              required
              value={cuando}
              onChange={(e) => setCuando(e.target.value)}
            />
          </label>

          <label>
            Mes que cubre
            <input
              type="month"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            />
          </label>

          <label>
            Pagado hasta
            {/*
              La fecha que cuenta para el aviso y la gracia (§A.2.1). Se propone
              según el mes y la periodicidad, y se cambia si han pagado otra cosa.
            */}
            <input
              type="date"
              required
              value={hastaPropuesto}
              onChange={(e) => setHasta(e.target.value)}
            />
          </label>

          <label>
            Referencia
            <input
              maxLength={200}
              placeholder="El concepto de la transferencia"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </label>
        </div>

        <div className="nota">
          <strong>«Pagado hasta» es lo que cuenta</strong>: pasada esa fecha el
          club tiene sus días de gracia con aviso, y después su panel pasa a
          solo lectura. Para un pago trimestral o anual, pon el último día que
          cubre. Deja el mes vacío si no cubre uno concreto: un ajuste o una
          diferencia.
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={anotar.isPending}>
            {anotar.isPending ? 'Anotando…' : 'Anotar el cobro'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={anotar.isPending}
            onClick={() => {
              setAnotando(false);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La ficha ─────────────────────────────────────────────────────────────
  const riesgo = c.risk_level ? RIESGOS[c.risk_level] : undefined;

  return (
    <section>
      <div className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Clubes
        </button>
        {/*
          Entrar a mirar deja rastro: motivo, caducidad y aviso al club. El botón
          está aquí y no escondido porque lo que se esconde se rodea.
        */}
        <button type="button" onClick={() => alEntrarEnSoporte(clubId)}>
          Entrar en modo soporte
        </button>
      </div>

      <h1>
        {c.trade_name}{' '}
        <span className="pastilla pastilla-left">
          {etiquetaDeEstadoDeClub(c.status)}
        </span>
        {riesgo && (
          <span className={`pastilla ${riesgo.clase} pastilla-left`}>
            {riesgo.etiqueta}
          </span>
        )}
        {c.is_demo && <span className="pastilla pastilla-left">Demo</span>}
      </h1>

      {c.is_demo && (
        <p className="aviso" role="note">
          <strong>Es el club de demostración.</strong> Con él revisan la app
          Apple y Google, y se hacen las demos comerciales: no le cambies el
          plan, el dueño ni las contraseñas. No cuenta en las cifras ni en la
          salud de los clubes.
        </p>
      )}

      <dl className="datos">
        <div>
          <dt>Slug</dt>
          <dd>
            <code>{c.slug}</code>
          </dd>
        </div>
        <div>
          <dt>Dueño</dt>
          <dd>
            {c.owner ? (
              <button
                type="button"
                className="enlace-fila"
                onClick={() => alAbrirPersona(c.owner!.user_id)}
              >
                {c.owner.name}
              </button>
            ) : (
              '—'
            )}
            {c.owner?.pending && (
              <InvitacionDelDueno clubId={clubId} correo={c.owner.email} />
            )}
            {c.owner && (
              <CambioDeDueno clubId={clubId} actual={c.owner.email} />
            )}
          </dd>
        </div>
        <div>
          <dt>Correo</dt>
          <dd>{c.email ?? c.owner?.email ?? '—'}</dd>
        </div>
        <div>
          <dt>Teléfono</dt>
          <dd>{c.phone ?? '—'}</dd>
        </div>
        <div>
          <dt>Alumnos</dt>
          <dd>{c.student_count}</dd>
        </div>
        <div>
          <dt>Sedes y personal</dt>
          <dd>
            {c.venue_count} · {c.staff_count}
          </dd>
        </div>
        <div>
          <dt>Alta</dt>
          <dd>{fecha(c.onboarded_at ?? c.created_at)}</dd>
        </div>
        <div>
          <dt>Contrato de datos</dt>
          {/* Sin DPA firmado no se puede tratar dato de menores. Es lo primero que pregunta un club serio. */}
          <dd>
            {c.dpa_signed_at
              ? `${c.dpa_version ?? '—'} · ${fecha(c.dpa_signed_at)}`
              : 'Sin firmar'}
          </dd>
        </div>
      </dl>

      <h2 className="seccion">Si lo usan · últimos 30 días</h2>

      {uso.isPending && <Cargando filas={2} etiqueta="Cargando el uso" />}

      {uso.data && (
        <>
          <dl className="datos">
            <div>
              <dt>Clases dadas</dt>
              <dd>{uso.data.sessions_held}</dd>
            </div>
            <div>
              <dt>Listas pasadas</dt>
              {/* La cifra que dice si el club **usa** Kiap o solo lo tiene contratado. */}
              <dd>{uso.data.roll_calls}</dd>
            </div>
            <div>
              <dt>Alumnos activos</dt>
              <dd>{uso.data.active_students}</dd>
            </div>
            <div>
              <dt>Familias con la app</dt>
              <dd>
                {uso.data.guardians_with_app} de {uso.data.guardians}
              </dd>
            </div>
            <div>
              <dt>Avisos publicados</dt>
              <dd>{uso.data.announcements}</dd>
            </div>
            <div>
              <dt>Remesas lanzadas</dt>
              <dd>{uso.data.billing_runs}</dd>
            </div>
          </dl>

          <p className="tenue">
            Última actividad: {fechaYHora(uso.data.last_activity_at)}.{' '}
            {uso.data.roll_calls === 0 && (
              <strong>
                No han pasado lista ni una vez en 30 días: eso es una baja que
                aún no ha ocurrido.
              </strong>
            )}
          </p>
        </>
      )}

      <h2 className="seccion">Lo contratado</h2>

      <SuscripcionDelClub clubId={clubId} />

      <div className="titulo-fila">
        <h2 className="seccion">Lo que nos ha pagado</h2>
        <button type="button" onClick={() => setAnotando(true)}>
          Anotar un cobro
        </button>
      </div>

      {pagos.isPending && <Cargando filas={3} etiqueta="Cargando los cobros" />}

      {pagos.isError && (
        <PanelError
          problema={aProblema(pagos.error)}
          reintentar={() => void pagos.refetch()}
        />
      )}

      {resumen && resumen.standing !== 'none' && (
        <p>
          <span
            className={`pastilla ${SITUACIONES_DEL_PAGO[resumen.standing].clase}`}
          >
            {SITUACIONES_DEL_PAGO[resumen.standing].etiqueta}
          </span>{' '}
          Pagado hasta el <strong>{fecha(resumen.paid_through)}</strong>
          {resumen.standing === 'due' &&
            `. Gracia hasta el ${fecha(resumen.grace_ends_on)}: después, su panel pasa a solo lectura.`}
          {resumen.standing === 'lapsed' &&
            '. Su panel está en solo lectura; la app del maestro y la de familias siguen. Anotar el cobro lo abre en el acto.'}
        </p>
      )}

      {resumen && (
        <div className="aviso aviso-calma" role="status">
          <strong>{euros(resumen.collected_cents)} cobrados</strong> en{' '}
          {resumen.payments === 1 ? '1 pago' : `${resumen.payments} pagos`}.
          {resumen.last_paid_on
            ? ` El último, el ${fecha(resumen.last_paid_on)}.`
            : ' Todavía no ha pagado nada.'}
          {/*
            Lo cobrado no tiene por qué cuadrar con lo contratado, y decirlo evita
            la conclusión equivocada al ver dos cifras distintas.
          */}
          <p className="tenue">
            Es lo que ha entrado de verdad, de todo el histórico. No cuadra con
            el precio del plan y no tiene por qué: aquello es lo contratado.
          </p>
        </div>
      )}

      {pagos.data?.data.length === 0 ? (
        <p className="tenue">Sin cobros anotados.</p>
      ) : (
        pagos.data && (
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Entró</th>
                  <th>Cubre</th>
                  <th>Hasta</th>
                  <th className="num">Importe</th>
                  <th>Cómo</th>
                  <th>Referencia</th>
                  <th>Lo marcó</th>
                </tr>
              </thead>
              <tbody>
                {pagos.data.data.map((p) => (
                  <tr key={p.id}>
                    <td>{fecha(p.paid_on)}</td>
                    <td className="tenue">
                      {p.period ? fecha(p.period) : 'Ajuste'}
                    </td>
                    <td>{fecha(p.covers_until)}</td>
                    <td className="num">
                      <strong>{euros(p.amount_cents)}</strong>
                    </td>
                    <td className="tenue">
                      {etiquetaDeMetodoDePago(p.method)}
                    </td>
                    <td className="tenue">{p.reference ?? '—'}</td>
                    {/* Es dinero y lo marcó una persona: eso hay que poder auditarlo. */}
                    <td className="tenue">{p.recorded_by_email ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/*
        Y debajo, en este orden: **qué le ha pasado**, **qué se le puede hacer** y
        los interruptores.

        La cronología va primero de las tres porque es lo que se lee antes de
        llamar. Las acciones van después de haber leído: suspender un club es una
        decisión, y ponerla arriba invita a tomarla sin mirar. Los interruptores van
        al final porque casi nunca hay ninguno, y cuando hay uno se viene a buscarlo
        a propósito.
      */}
      <CronologiaDelClub clubId={clubId} />

      <AccionesDelClub
        clubId={clubId}
        nombre={c.trade_name}
        estado={c.status}
      />

      <InterruptoresDelClub clubId={clubId} />
    </section>
  );
}
